/**
 * The v3 run page's SSR seed (`/orca/v3/runs/$runId`).
 *
 * The run page's detail atom (`v3RunDetailAtomFamily` in `lib/atoms/v3-run.ts`)
 * is `hydratable` under {@link v3RunKey}. This reads `v3.runs.get` server-side
 * as the visitor and dehydrates the `RunDetail` under that key, so the page
 * paints the run on first byte instead of a loading frame.
 *
 * The read mirrors `loopInvocationServerFn` in `orca-ssr.ts`:
 *
 * - the bearer is the visitor's own `__session` cookie, sent only on this one
 *   server-to-api hop and never logged or dehydrated;
 * - the visitor's brand host rides along as `X-Forwarded-Host`, because
 *   `LoopActorMiddleware` picks the Clerk instance from it — without it an
 *   orcaloops.ai session is verified against the default brand's key, fails,
 *   the seed is dropped, and the server renders the loading frame under a
 *   client that then reads the run (React #418);
 * - the payload is `{ runId }` alone: the middleware scopes the read to the
 *   visitor's customer, and a run they don't own is `RecordNotFoundError`;
 * - any failure seeds nothing — never an SSR crash.
 *
 * The `RunDetail` is dehydrated exactly as read, encoded with the schema the
 * atom decodes with; nothing derived from a clock is added here.
 *
 * @since 1.0.0
 */
import type { Hydration } from "@effect-atom/atom-react";
import { createServerFn } from "@tanstack/react-start";
import { getCookie, getRequestHeaders } from "@tanstack/react-start/server";
import { Effect } from "effect";
import type { BeforeLoadArgs, BeforeLoadResult } from "./orca-ssr.ts";
import { forwardedHostFromHeaders } from "./server-auth.ts";
import { readRunDetail, seedRunDetail } from "./v3-run-read.ts";

/**
 * The server half. `readRunDetail` and `seedRunDetail` (in the server-only
 * `v3-run-read.ts`) are referenced only from this handler, which the TanStack
 * Start plugin strips from the client build — dropping the import with it, so
 * the rpc and schema graph stays out of the always-loaded entry chunk. Keep
 * every `@fm/api/rpc` / `@fm/v3` reference out of this module's top level
 * (`v3-run-ssr.chunk.test.ts`).
 */
const v3RunServerFn = createServerFn({ method: "GET" })
  .inputValidator((runId: string): string => runId)
  // oxlint-disable-next-line eslint-js/no-restricted-syntax -- TanStack Start server functions require async/await
  .handler(async (ctx): Promise<BeforeLoadResult> => {
    const token = getCookie("__session") ?? null;
    if (!token) return { dehydratedAtoms: [] };
    // oxlint-disable-next-line eslint-js/no-restricted-syntax -- Effect.runPromise requires await
    const detail = await Effect.runPromise(
      readRunDetail(ctx.data, token, forwardedHostFromHeaders(getRequestHeaders())),
    );
    // oxlint-disable-next-line eslint-js/no-restricted-syntax -- hydrateAtoms returns a Promise
    return await seedRunDetail(ctx.data, detail)(ctx);
  });

const mergeAtoms = (
  parent: Array<Hydration.DehydratedAtom> | undefined,
  next: Array<Hydration.DehydratedAtom>,
): Array<Hydration.DehydratedAtom> => [...(parent ?? []), ...next];

/**
 * The run page's seeder: with a `runId` param, the server read's atoms after
 * the parent's; without one, the parent's unchanged and no request.
 *
 * @since 1.0.0
 */
// oxlint-disable-next-line eslint-js/no-restricted-syntax -- TanStack Start server functions require async/await
export const v3RunBeforeLoad = async (args: BeforeLoadArgs): Promise<BeforeLoadResult> => {
  const runId = args.params?.runId;
  if (!runId) {
    return { dehydratedAtoms: args.context?.dehydratedAtoms ?? [] };
  }
  // oxlint-disable-next-line eslint-js/no-restricted-syntax -- Server function returns Promise
  const result = await v3RunServerFn({ data: runId });
  return { dehydratedAtoms: mergeAtoms(args.context?.dehydratedAtoms, result.dehydratedAtoms) };
};
